import { verbPhraseWording } from "@amoreg/shared/connectorActions";
import { bareToolName, toolNamespace } from "@amoreg/shared/toolName";

import type { WorkLogEntry } from "../../session-logic";
import { connectorToolHeading, resolveConnectorToolCall } from "./connectorToolPresentation";
import { jsonSummaryPreview } from "./toolInputText";

export type NamedToolAction =
  | "knowledge"
  | "document"
  | "file-search"
  | "history"
  | "skill"
  | "integration"
  | "discovery"
  | "agents"
  | "browser"
  | "amoreg"
  | "workflows"
  | "workspace";

interface NamedToolPresentation {
  readonly action: NamedToolAction;
  readonly title: string;
  readonly running: string;
  readonly completed: string;
}

/**
 * Eve's workflow tools, each as one line naming the workflow: from the
 * call's result once it lands (`workflowOutcome`), never its id.
 */
const WORKFLOW_CALLS: Readonly<
  Record<string, { readonly doing: string; readonly unnamed: string }>
> = {
  workflows_list: { doing: "Listing workflows", unnamed: "Listing workflows" },
  workflows_read: { doing: "Reading", unnamed: "Reading a workflow" },
  workflows_edit: { doing: "Editing", unnamed: "Editing a workflow" },
  workflows_draft: { doing: "Editing", unnamed: "Editing a workflow" },
  workflows_create: { doing: "Creating", unnamed: "Creating a workflow" },
  workflows_undo: { doing: "Undoing a change to", unnamed: "Undoing a change" },
};

// Follow upstream's named-tool presentation: provider identity selects the
// activity, then lifecycle selects the verb. Legacy titles support saved chats
// from before Eve's tool names were retained by the adapter.
const TOOLS: Readonly<Record<string, NamedToolPresentation>> = {
  ...Object.fromEntries(
    Object.entries(WORKFLOW_CALLS).map(([name, call]) => [
      name,
      { action: "workflows" as const, title: call.unnamed, running: call.unnamed, completed: call.unnamed },
    ]),
  ),
  get_information: {
    action: "knowledge",
    title: "Search knowledge base",
    running: "Searching knowledge base",
    completed: "Searched knowledge base",
  },
  get_document: {
    action: "document",
    title: "Read document",
    running: "Reading document",
    completed: "Read document",
  },
  edit_org_context: {
    action: "amoreg",
    title: "Update organisation context",
    running: "Updating organisation context",
    completed: "Updated organisation context",
  },
  files_search: {
    action: "file-search",
    title: "Search files",
    running: "Searching files",
    completed: "Searched files",
  },
  session_search: {
    action: "history",
    title: "Search conversations",
    running: "Searching conversations",
    completed: "Searched conversations",
  },
  session_read: {
    action: "history",
    title: "Read conversation",
    running: "Reading conversation",
    completed: "Read conversation",
  },
  load_skill: {
    action: "skill",
    title: "Load skill",
    running: "Loading skill",
    completed: "Loaded skill",
  },
  // Discovery: looking a tool up, not using one. Once the call it prepared
  // has run, these fold into it (see `isToolDiscoveryEntry`).
  connection_search: {
    action: "discovery",
    title: "Find tools",
    running: "Finding tools",
    completed: "Found tools",
  },
  plugins_list_accounts: {
    action: "discovery",
    title: "Check connected apps",
    running: "Checking connected apps",
    completed: "Checked connected apps",
  },
  plugins_search_tools: {
    action: "discovery",
    title: "Find app actions",
    running: "Finding app actions",
    completed: "Found app actions",
  },
  workflow: {
    action: "agents",
    title: "Run workflow",
    running: "Running workflow",
    completed: "Ran workflow",
  },
  plugins_execute_action: {
    action: "integration",
    title: "Run app action",
    running: "Running app action",
    completed: "Ran app action",
  },
  plugins_execute_tool: {
    action: "integration",
    title: "Run connected tool",
    running: "Running connected tool",
    completed: "Ran connected tool",
  },
};

/** `Editing Weekly findings digest`; null for any other tool. */
export function workflowCallHeading(
  entry: Pick<WorkLogEntry, "toolName" | "itemType" | "toolData" | "workflowOutcome">,
): string | null {
  if (entry.itemType !== "dynamic_tool_call" && entry.itemType !== "mcp_tool_call") return null;
  if (entry.toolName === undefined) return null;
  const call = WORKFLOW_CALLS[normalizedToolName(entry.toolName)];
  if (call === undefined) return null;
  if (call.doing === call.unnamed) return call.doing;
  const input = entry.toolData as { readonly name?: unknown } | undefined;
  const name =
    entry.workflowOutcome?.name ??
    (normalizedToolName(entry.toolName) === "workflows_create" && typeof input?.name === "string"
      ? input.name.trim()
      : "");
  return name ? `${call.doing} ${name}` : call.unnamed;
}

const LEGACY_NAMES: Readonly<Record<string, string>> = {
  "knowledge base": "get_information",
  "document reader": "get_document",
  skill: "load_skill",
};

function normalizedToolName(value: string): string {
  // eve qualifies a connection's tools with its namespace
  // (`workspace__plugins_execute_action`); the presentation is the tool's.
  return bareToolName(value.trim())
    .replace(/\s+(?:complete|completed|started)$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

export function resolveNamedToolPresentation(
  entry: Pick<WorkLogEntry, "toolName" | "toolTitle" | "label" | "itemType" | "toolData">,
): NamedToolPresentation | null {
  if (entry.itemType !== "dynamic_tool_call" && entry.itemType !== "mcp_tool_call") return null;
  const legacyTitle = (entry.toolTitle ?? entry.label)
    .trim()
    .replace(/\s+(?:complete|completed|started)$/i, "")
    .toLowerCase();
  const legacyName = Object.hasOwn(LEGACY_NAMES, legacyTitle)
    ? LEGACY_NAMES[legacyTitle]
    : undefined;
  const name = normalizedToolName(entry.toolName ?? legacyName ?? entry.toolTitle ?? entry.label);
  if (Object.hasOwn(TOOLS, name)) return TOOLS[name]!;
  // Eve's native skill action uses the generic tool name and input.skill.
  if (
    name === "tool" &&
    entry.toolData &&
    typeof entry.toolData === "object" &&
    "skill" in entry.toolData &&
    typeof entry.toolData.skill === "string"
  )
    return TOOLS.load_skill!;

  // These are the existing Eve workspace connection's tool namespaces, not
  // inferred from arbitrary arguments, output text, or a substring match.
  if (name.startsWith("governance_")) return amoregToolPresentation(name);
  const action = name.startsWith("preview_")
    ? "browser"
    : name.startsWith("plainsail_")
      ? "workspace"
      : null;
  if (action === null) return null;
  const title = capitalise(name.replace(/^(?:preview|plainsail)_/, "").replaceAll("_", " "));
  return { action, title, running: title, completed: title };
}

/**
 * A call the agent made through the Amoreg workspace MCP connection, which
 * eve qualifies with its `workspace` namespace. The row wears the Amoreg mark
 * instead of a generic wrench.
 */
export function isWorkspaceToolCall(
  entry: Pick<WorkLogEntry, "toolName" | "itemType">,
): boolean {
  if (entry.itemType !== "dynamic_tool_call" && entry.itemType !== "mcp_tool_call") return false;
  return entry.toolName !== undefined && toolNamespace(entry.toolName) === "workspace";
}

const capitalise = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Amoreg's own workspace tools whose verb-object name reads badly as a
 * phrase; the rest are worded from the name (`governance_list_policies` →
 * "Listing policies" / "Listed policies").
 */
const AMOREG_TOOL_WORDING: Readonly<
  Record<string, Pick<NamedToolPresentation, "title" | "running" | "completed">>
> = {
  governance_get_context: {
    title: "read organisation context",
    running: "Reading organisation context",
    completed: "Read organisation context",
  },
  governance_edit_context: {
    title: "update organisation context",
    running: "Updating organisation context",
    completed: "Updated organisation context",
  },
  governance_search_knowledge: {
    title: "search knowledge base",
    running: "Searching knowledge base",
    completed: "Searched knowledge base",
  },
  governance_get_policy_diff: {
    title: "compare policy versions",
    running: "Comparing policy versions",
    completed: "Compared policy versions",
  },
  governance_create_version: {
    title: "create policy version",
    running: "Creating policy version",
    completed: "Created policy version",
  },
  governance_recommend: {
    title: "add recommendation",
    running: "Adding recommendation",
    completed: "Added recommendation",
  },
  governance_decide_finding: {
    title: "decide on finding",
    running: "Deciding on finding",
    completed: "Decided on finding",
  },
  governance_decide_recommendation: {
    title: "decide on recommendation",
    running: "Deciding on recommendation",
    completed: "Decided on recommendation",
  },
  governance_set_member_admin: {
    title: "change member's admin role",
    running: "Changing member's admin role",
    completed: "Changed member's admin role",
  },
  governance_mark_monitor_checked: {
    title: "mark monitor as checked",
    running: "Marking monitor as checked",
    completed: "Marked monitor as checked",
  },
};

/** One of Amoreg's own workspace tools, worded as the action it takes. */
function amoregToolPresentation(name: string): NamedToolPresentation {
  const words = name
    .replace(/^governance_/, "")
    .split("_")
    .filter((word) => word.length > 0);
  const wording = Object.hasOwn(AMOREG_TOOL_WORDING, name)
    ? AMOREG_TOOL_WORDING[name]!
    : verbPhraseWording(words);
  if (wording === null) {
    // No recognisable verb: the name itself, as before.
    const title = capitalise(words.join(" "));
    return { action: "amoreg", title, running: title, completed: title };
  }
  return {
    action: "amoreg",
    title: capitalise(wording.title),
    running: wording.running,
    completed: wording.completed,
  };
}

const DISCOVERY_TOOLS: ReadonlySet<string> = new Set([
  "connection_search",
  "plugins_list_accounts",
  "plugins_search_tools",
]);

/**
 * A call that only looked a tool up: eve's `connection_search`, and the
 * workspace's account check and action search. It is how the agent reaches
 * Gmail, not something it did there, so once a later call has used what it
 * found, the row folds away and the Gmail search reads as the one step.
 */
export function isToolDiscoveryEntry(entry: Pick<WorkLogEntry, "toolName" | "itemType">): boolean {
  return toolDiscoveryName(entry) !== null;
}

type ToolDiscoveryName = "connection_search" | "plugins_list_accounts" | "plugins_search_tools";

/** Which lookup a row is, when it is one. */
export function toolDiscoveryName(
  entry: Pick<WorkLogEntry, "toolName" | "itemType">,
): ToolDiscoveryName | null {
  if (entry.itemType !== "dynamic_tool_call" && entry.itemType !== "mcp_tool_call") return null;
  if (entry.toolName === undefined) return null;
  const name = normalizedToolName(entry.toolName);
  return DISCOVERY_TOOLS.has(name) ? (name as ToolDiscoveryName) : null;
}

export interface WorkflowAgentBrief {
  /** The agent's name as the script calls it (`worker_flash`). */
  readonly name: string;
  /** Its title as the Agents panel shows it ("Worker flash"). */
  readonly title: string;
  readonly brief: string | null;
}

/**
 * A JavaScript string or template literal starting at `start`: its value
 * with escapes decoded, and the index just past it. A template keeps its
 * `${…}` parts as written. Null when `start` is not a quote.
 */
function readStringLiteral(
  source: string,
  start: number,
): { readonly value: string; readonly end: number } | null {
  const quote = source[start];
  if (quote !== '"' && quote !== "'" && quote !== "`") return null;
  let value = "";
  let index = start + 1;
  while (index < source.length) {
    const char = source[index]!;
    if (char === quote) return { value, end: index + 1 };
    if (char === "\\") {
      const decoded = decodeEscape(source, index);
      value += decoded.text;
      index = decoded.end;
      continue;
    }
    if (quote === "`" && char === "$" && source[index + 1] === "{") {
      const close = skipBalanced(source, index + 1);
      value += source.slice(index, close);
      index = close;
      continue;
    }
    if (quote !== "`" && (char === "\n" || char === "\r")) return null;
    value += char;
    index += 1;
  }
  return null;
}

const SIMPLE_ESCAPES: Readonly<Record<string, string>> = {
  n: "\n",
  r: "\r",
  t: "\t",
  b: "\b",
  f: "\f",
  v: "\v",
};

/** The escape sequence at `index` (a backslash) as JavaScript reads it. */
function decodeEscape(source: string, index: number): { text: string; end: number } {
  const next = source[index + 1] ?? "";
  if (Object.hasOwn(SIMPLE_ESCAPES, next)) return { text: SIMPLE_ESCAPES[next]!, end: index + 2 };
  if (next === "0" && !/[0-9]/.test(source[index + 2] ?? "")) return { text: "\0", end: index + 2 };
  // A line continuation: the backslash and the line break read as nothing.
  if (next === "\r") return { text: "", end: source[index + 2] === "\n" ? index + 3 : index + 2 };
  if (next === "\n" || next === " " || next === " ") return { text: "", end: index + 2 };
  const hex = (digits: string) =>
    /^[0-9a-fA-F]+$/.test(digits) ? Number.parseInt(digits, 16) : null;
  const codePoint = (value: number | null, end: number) =>
    value !== null && value <= 0x10ffff ? { text: String.fromCodePoint(value), end } : null;
  if (next === "x") {
    const decoded = codePoint(hex(source.slice(index + 2, index + 4)), index + 4);
    if (decoded && source.slice(index + 2, index + 4).length === 2) return decoded;
  }
  if (next === "u") {
    if (source[index + 2] === "{") {
      const close = source.indexOf("}", index + 3);
      const decoded =
        close === -1 ? null : codePoint(hex(source.slice(index + 3, close)), close + 1);
      if (decoded) return decoded;
    } else {
      const digits = source.slice(index + 2, index + 6);
      const decoded = digits.length === 4 ? codePoint(hex(digits), index + 6) : null;
      if (decoded) return decoded;
    }
  }
  return { text: next, end: index + 2 };
}

/** Index just past the bracket that closes the one at `open`, skipping strings and comments. */
function skipBalanced(source: string, open: number): number {
  let depth = 0;
  let index = open;
  while (index < source.length) {
    const skipped = skipNonCode(source, index);
    if (skipped !== index) {
      index = skipped;
      continue;
    }
    const char = source[index]!;
    if (char === "{" || char === "(" || char === "[") depth += 1;
    else if (char === "}" || char === ")" || char === "]") {
      depth -= 1;
      if (depth === 0) return index + 1;
    }
    index += 1;
  }
  return source.length;
}

/** Past a string literal or comment at `index`; `index` itself when there is none. */
function skipNonCode(source: string, index: number): number {
  const char = source[index];
  if (char === '"' || char === "'" || char === "`") {
    return readStringLiteral(source, index)?.end ?? source.length;
  }
  if (char === "/" && source[index + 1] === "/") {
    const end = source.indexOf("\n", index);
    return end === -1 ? source.length : end + 1;
  }
  if (char === "/" && source[index + 1] === "*") {
    const end = source.indexOf("*/", index + 2);
    return end === -1 ? source.length : end + 2;
  }
  return index;
}

function skipSpace(source: string, index: number): number {
  let cursor = index;
  while (cursor < source.length) {
    const skipped = skipNonCode(source, cursor);
    if (skipped !== cursor && source[cursor] === "/") {
      cursor = skipped;
      continue;
    }
    if (!/\s/.test(source[cursor]!)) break;
    cursor += 1;
  }
  return cursor;
}

/**
 * The top-level `message` of the object literal at `open` (a `{`): nested
 * objects — an `outputSchema` with its own `message` field — are skipped.
 */
function readBriefFromOptions(source: string, open: number): string | null {
  const close = skipBalanced(source, open);
  let index = open + 1;
  let expectingKey = true;
  while (index < close - 1) {
    index = skipSpace(source, index);
    const char = source[index];
    if (char === undefined || index >= close - 1) break;
    if (char === ",") {
      expectingKey = true;
      index += 1;
      continue;
    }
    if (expectingKey) {
      let key: string | null = null;
      let afterKey = index;
      const literal = readStringLiteral(source, index);
      if (literal) {
        key = literal.value;
        afterKey = literal.end;
      } else {
        const identifier = /^[A-Za-z_$][\w$]*/.exec(source.slice(index));
        if (identifier) {
          key = identifier[0];
          afterKey = index + identifier[0].length;
        }
      }
      expectingKey = false;
      if (key !== null) {
        const colon = skipSpace(source, afterKey);
        if (source[colon] === ":") {
          const valueStart = skipSpace(source, colon + 1);
          if (key === "message") return readStringLiteral(source, valueStart)?.value ?? null;
          index = valueStart;
          continue;
        }
        index = afterKey;
        continue;
      }
    }
    if (char === "{" || char === "(" || char === "[") {
      index = skipBalanced(source, index);
      continue;
    }
    const skipped = skipNonCode(source, index);
    index = skipped !== index ? skipped : index + 1;
  }
  return null;
}

/** Every `ctx.agent(name, { message })` the script itself calls — never one quoted in a string. */
function scanWorkflowAgents(script: string): Array<{ name: string; brief: string | null }> {
  const agents: Array<{ name: string; brief: string | null }> = [];
  let index = 0;
  while (index < script.length) {
    const skipped = skipNonCode(script, index);
    if (skipped !== index) {
      index = skipped;
      continue;
    }
    const call = /^ctx\s*\.\s*agent\s*\(/.exec(script.slice(index, index + 32));
    if (!call || /[\w$.]/.test(script[index - 1] ?? "")) {
      index += 1;
      continue;
    }
    const argument = skipSpace(script, index + call[0].length);
    const name = readStringLiteral(script, argument);
    index = argument;
    if (!name || name.value.trim().length === 0) continue;
    const comma = skipSpace(script, name.end);
    let brief: string | null = null;
    if (script[comma] === ",") {
      const options = skipSpace(script, comma + 1);
      if (script[options] === "{") brief = readBriefFromOptions(script, options);
    }
    agents.push({ name: name.value.trim(), brief: brief?.trim() || null });
    index = name.end;
  }
  return agents;
}

const workflowAgentsCache = new WeakMap<object, ReadonlyArray<WorkflowAgentBrief>>();

/**
 * The agents an eve `workflow` call launches, read off its script: each
 * `ctx.agent(name, { message })` is one, with its brief. The script is the
 * model's own code, so this only ever labels the row; the Agents panel's
 * roster is what reports on the agents themselves.
 */
export function workflowAgentBriefs(
  entry: Pick<WorkLogEntry, "toolName" | "itemType" | "toolData">,
): ReadonlyArray<WorkflowAgentBrief> | null {
  if (entry.itemType !== "dynamic_tool_call" || entry.toolName === undefined) return null;
  if (normalizedToolName(entry.toolName) !== "workflow") return null;
  const input =
    typeof entry.toolData === "object" && entry.toolData !== null
      ? (entry.toolData as Record<string, unknown>)
      : null;
  if (input === null) return [];
  const cached = workflowAgentsCache.get(input);
  if (cached) return cached;
  const script = typeof input.js === "string" ? input.js : "";
  const agents: WorkflowAgentBrief[] = scanWorkflowAgents(script).map(({ name, brief }) => ({
    name,
    title: agentTitle(name),
    brief,
  }));
  workflowAgentsCache.set(input, agents);
  return agents;
}

function agentTitle(name: string): string {
  const words = name.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function workflowHeading(
  agents: ReadonlyArray<WorkflowAgentBrief>,
  status: WorkLogEntry["toolLifecycleStatus"],
): string | null {
  if (agents.length === 0) return null;
  const subject = `${agents.length} ${agents.length === 1 ? "subagent" : "subagents"}`;
  switch (status) {
    case "inProgress":
      return `Kicking off ${subject}`;
    case "completed":
      return `Ran ${subject}`;
    case "failed":
      return `Failed to run ${subject}`;
    case "declined":
      return `Declined to run ${subject}`;
    case "stopped":
      return `Stopped: ${subject}`;
    default:
      return `Run ${subject}`;
  }
}

export function namedToolHeading(entry: WorkLogEntry): string | null {
  const workflowCall = workflowCallHeading(entry);
  if (workflowCall) return workflowCall;
  // A call into a connected app reads as what it did there ("Searching
  // emails"), not as the workspace tool that carried it ("Running app action").
  const connectorCall = resolveConnectorToolCall(entry);
  if (connectorCall) return connectorToolHeading(connectorCall, entry.toolLifecycleStatus);
  const agents = workflowAgentBriefs(entry);
  const workflow = agents ? workflowHeading(agents, entry.toolLifecycleStatus) : null;
  if (workflow) return workflow;
  const tool = resolveNamedToolPresentation(entry);
  if (!tool) return null;
  if (tool.action === "skill") {
    const input = entry.toolData;
    const name =
      input && typeof input === "object"
        ? "skill" in input && typeof input.skill === "string"
          ? input.skill
          : "name" in input && typeof input.name === "string"
            ? input.name
            : null
        : null;
    const labels: Record<string, string> = {
      "pdf-documents": "PDF",
      "docx-documents": "Word document",
      "xlsx-spreadsheets": "spreadsheet",
      "pptx-presentations": "presentation",
    };
    if (name && Object.hasOwn(labels, name)) {
      const subject = `${labels[name]} instructions`;
      if (entry.toolLifecycleStatus === "inProgress") return `Loading ${subject}`;
      if (entry.toolLifecycleStatus === "completed") return `Loaded ${subject}`;
      if (entry.toolLifecycleStatus === "failed") return `Failed to load ${subject}`;
      if (entry.toolLifecycleStatus === "declined") return `Declined to load ${subject}`;
      if (entry.toolLifecycleStatus === "stopped") return `Stopped loading ${subject}`;
      return `Load ${subject}`;
    }
  }
  switch (entry.toolLifecycleStatus) {
    case "inProgress":
      return tool.running;
    case "completed":
      return tool.completed;
    case "failed":
      return `Failed to ${tool.title.toLowerCase()}`;
    case "declined":
      return `Declined to ${tool.title.toLowerCase()}`;
    case "stopped":
      return `Stopped: ${tool.title}`;
    default:
      return tool.title;
  }
}

export function namedToolGroupLabel(action: NamedToolAction, count: number): string {
  const times = `${count} ${count === 1 ? "time" : "times"}`;
  switch (action) {
    case "knowledge":
      return `Searched knowledge base ${times}`;
    case "document":
      return `Read ${count} ${count === 1 ? "document" : "documents"}`;
    case "file-search":
      return `Searched files ${times}`;
    case "history":
      return `Checked conversations ${times}`;
    case "skill":
      return `Loaded ${count} ${count === 1 ? "skill" : "skills"}`;
    case "integration":
      return `Used connected apps ${times}`;
    case "discovery":
      return `Looked up tools ${times}`;
    case "agents":
      return `Ran ${count} ${count === 1 ? "workflow" : "workflows"}`;
    case "browser":
      return `Used browser ${times}`;
    case "amoreg":
      return `Used Amoreg tools ${times}`;
    case "workspace":
      return `Used workspace tools ${times}`;
    case "workflows":
      return `Worked on workflows ${times}`;
  }
}

// "Workspace plugins execute action: {…}": the server's progress line for a
// subagent's latest call, the tool's title and its one-line summary.
const PROGRESS_LINE = /^([^:\n]{1,80}): (\{[\s\S]*)$/;

/**
 * A subagent's progress line as text. The line is written server-side from
 * the call's title and its summary, which is the input as JSON when the call
 * has no query or path; a Gmail search then reads "Searching emails ·
 * altruist" as it does in the parent's own timeline.
 */
export function describeToolProgress(line: string): string {
  const match = PROGRESS_LINE.exec(line.trim());
  if (!match) return line;
  const title = match[1]!.trim();
  const summary = match[2]!;
  const words = title.toLowerCase().split(/\s+/);
  const toolName =
    words[0] === "workspace" ? `workspace__${words.slice(1).join("_")}` : words.join("_");
  const connectorCall = resolveConnectorToolCall({
    itemType: "dynamic_tool_call",
    toolName,
    toolData: undefined,
    detail: summary,
  });
  if (connectorCall) {
    const heading = connectorToolHeading(connectorCall, "inProgress");
    return connectorCall.hint ? `${heading} · ${connectorCall.hint}` : heading;
  }
  const preview = jsonSummaryPreview(summary);
  return preview ? `${title} · ${preview}` : title;
}
